import React from "react";
import { motion } from "framer-motion";
import { Check, Award, Route, Users, Sparkles, ArrowRight } from "lucide-react";
import { Reveal, MagneticButton } from "./shared";
import { PLANS, INCLUDED } from "../../lib/data";

const ICONS = { award: Award, route: Route, users: Users, sparkles: Sparkles };

export const Membership = ({ onPlan, onNav }) => (
  <>
    {/* ─── Plans (dark premium band) ─── */}
    <section className="section dark" id="membership" data-testid="membership-section">
      <div className="container">
        <div style={{ textAlign: "center", maxWidth: 780, margin: "0 auto" }}>
          <Reveal><p className="eyebrow center" style={{ color: "var(--gold)", justifyContent: "center", display: "flex" }}>Membership</p></Reveal>
          <Reveal delay={0.05}>
            <h2 className="section-title" style={{ marginTop: 16, color: "#fff" }}>
              Membership <span className="accent">Plans</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="section-sub" style={{ color: "rgba(255,255,255,0.72)", margin: "18px auto 0" }}>
              Simple, transparent memberships designed to help you stay consistent, build confidence,
              and achieve your fitness goals through structured martial arts training.
            </p>
          </Reveal>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 30, marginTop: 60, alignItems: "stretch" }}
          className="plans-grid">
          {PLANS.map((p, i) => (
            <Reveal key={p.key} delay={i * 0.1} y={50}>
              <motion.div whileHover={{ y: -10 }} transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                data-testid={`plan-card-${i}`} className="plan-card-v2"
                style={{
                  position: "relative", height: "100%", borderRadius: "var(--radius-lg)", padding: "44px 40px",
                  background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.10)",
                  backdropFilter: "blur(10px)", WebkitBackdropFilter: "blur(10px)",
                  overflow: "hidden", display: "flex", flexDirection: "column",
                }}>
                {/* corner brand glow */}
                <span aria-hidden="true" style={{ position: "absolute", top: -90, right: -90, width: 240, height: 240,
                  background: "radial-gradient(circle,rgba(242,169,59,0.22),transparent 70%)" }} />

                <span style={{ display: "inline-flex", alignItems: "center", alignSelf: "flex-start",
                  padding: "7px 14px", borderRadius: 100, background: "rgba(242,169,59,0.14)",
                  border: "1px solid rgba(242,169,59,0.4)", color: "var(--gold)",
                  fontSize: 11.5, fontWeight: 800, letterSpacing: "0.14em", textTransform: "uppercase",
                  position: "relative", zIndex: 1 }}>{p.badge}</span>

                <h3 style={{ fontSize: 42, color: "#fff", marginTop: 18, position: "relative", zIndex: 1, lineHeight: 1 }}>{p.title}</h3>

                <div style={{ display: "flex", alignItems: "baseline", gap: 6, marginTop: 20, position: "relative", zIndex: 1 }}>
                  <span style={{ fontSize: 22, color: "var(--gold)", fontWeight: 800 }}>₹</span>
                  <span style={{ fontFamily: "'Bebas Neue'", fontSize: 68, color: "#fff", lineHeight: 1 }}>{p.price}</span>
                  <span style={{ fontSize: 13.5, color: "rgba(255,255,255,0.58)", letterSpacing: "0.08em",
                    textTransform: "uppercase", marginLeft: 8 }}>{p.period}</span>
                </div>

                <p style={{ marginTop: 18, fontSize: 14.5, color: "rgba(255,255,255,0.72)",
                  lineHeight: 1.65, position: "relative", zIndex: 1 }}>{p.description}</p>

                <span style={{ display: "block", height: 1, background: "rgba(255,255,255,0.10)",
                  margin: "24px 0 22px", position: "relative", zIndex: 1 }} />

                <ul style={{ display: "grid", gap: 12, position: "relative", zIndex: 1 }}>
                  {p.features.map((f) => (
                    <li key={f} style={{ display: "flex", gap: 12, alignItems: "flex-start",
                      fontSize: 14.5, color: "rgba(255,255,255,0.88)" }}>
                      <span style={{ width: 22, height: 22, borderRadius: "50%", background: "rgba(242,169,59,0.18)",
                        display: "flex", alignItems: "center", justifyContent: "center", flex: "none", marginTop: 1 }}>
                        <Check size={13} color="var(--gold)" />
                      </span>
                      {f}
                    </li>
                  ))}
                </ul>

                <MagneticButton onClick={() => onPlan(p.prefill)} data-testid={`plan-select-${i}`}
                  className="btn btn-gold"
                  style={{ width: "100%", justifyContent: "center", marginTop: 30, position: "relative", zIndex: 1 }}>
                  Book FREE Trial <ArrowRight size={16} />
                </MagneticButton>
              </motion.div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    {/* ─── What's Included ─── */}
    <section className="section alt" id="included" data-testid="included-section" style={{ paddingTop: 100, paddingBottom: 100 }}>
      <div className="container">
        <div style={{ textAlign: "center", maxWidth: 780, margin: "0 auto" }}>
          <Reveal><p className="eyebrow center" style={{ justifyContent: "center", display: "flex" }}>Every Membership</p></Reveal>
          <Reveal delay={0.05}>
            <h2 className="section-title" style={{ marginTop: 14 }}>
              What's included <span className="accent">with every membership</span>
            </h2>
          </Reveal>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 24, marginTop: 56 }} className="included-grid">
          {INCLUDED.map((f, i) => {
            const Icon = ICONS[f.icon];
            return (
              <Reveal key={f.title} delay={(i % 4) * 0.08} y={40}>
                <motion.div whileHover={{ y: -8 }} transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                  data-testid={`included-card-${i}`} className="included-card"
                  style={{ background: "#fff", borderRadius: "var(--radius-lg)", padding: "32px 26px",
                    boxShadow: "var(--shadow-soft)", border: "1px solid rgba(17,17,17,0.05)",
                    height: "100%", display: "flex", flexDirection: "column" }}>
                  <span style={{ width: 54, height: 54, borderRadius: 16, background: "rgba(242,169,59,0.16)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    color: "var(--red)", marginBottom: 20 }}>
                    <Icon size={26} />
                  </span>
                  <h4 style={{ fontFamily: "'Inter'", fontWeight: 800, fontSize: 17, color: "var(--black)",
                    letterSpacing: "0.01em", marginBottom: 10 }}>{f.title}</h4>
                  <p style={{ fontSize: 14.5, color: "var(--muted)", lineHeight: 1.65 }}>{f.desc}</p>
                </motion.div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>

    {/* ─── Start Your Journey (premium CTA) ─── */}
    <section className="section" id="start-journey" data-testid="cta-section" style={{ paddingTop: 40, paddingBottom: 120 }}>
      <div className="container">
        <Reveal>
          <div style={{ position: "relative", overflow: "hidden", borderRadius: 26,
            background: "linear-gradient(135deg,#0E0E0E 0%, #2A0A0E 55%, #C1121F 130%)",
            boxShadow: "var(--shadow-strong)", padding: "72px 60px", color: "#fff" }}
            className="cta-card">
            <span aria-hidden="true" style={{ position: "absolute", top: -140, right: -100, width: 380, height: 380,
              background: "radial-gradient(circle,rgba(242,169,59,0.3),transparent 70%)" }} />
            <span aria-hidden="true" style={{ position: "absolute", bottom: -160, left: -120, width: 380, height: 380,
              background: "radial-gradient(circle,rgba(193,18,31,0.55),transparent 70%)" }} />
            <span aria-hidden="true" style={{ position: "absolute", top: 0, left: 0, right: 0, height: 5,
              background: "linear-gradient(90deg,var(--red) 0 40%,var(--gold) 40% 60%,var(--red) 60% 100%)" }} />

            <div style={{ position: "relative", zIndex: 1, textAlign: "center", maxWidth: 780, margin: "0 auto" }}>
              <p className="eyebrow center" style={{ color: "var(--gold)", justifyContent: "center", display: "flex" }}>Ready to begin</p>
              <h2 style={{ fontSize: "clamp(42px,6vw,72px)", textTransform: "uppercase", color: "#fff",
                marginTop: 14, lineHeight: 0.95 }}>
                Start your <span style={{ color: "var(--gold)" }}>journey</span> today
              </h2>
              <p style={{ marginTop: 20, fontSize: 16.5, color: "rgba(255,255,255,0.82)", lineHeight: 1.7 }}>
                Whether you're looking to improve your fitness, gain confidence, build discipline, or simply try
                something new, Victorious Lions Academy welcomes beginners of all ages.
                <br /><em style={{ color: "var(--gold)", fontStyle: "normal", fontWeight: 700 }}>Experience your first class before making a commitment.</em>
              </p>
              <div style={{ display: "flex", gap: 14, justifyContent: "center", flexWrap: "wrap", marginTop: 34 }}>
                <MagneticButton onClick={() => onNav("contact")} data-testid="cta-trial-btn"
                  className="btn btn-gold"><span>Book FREE Trial</span> <ArrowRight size={17} /></MagneticButton>
                <MagneticButton onClick={() => onNav("contact")} data-testid="cta-contact-btn"
                  className="btn btn-outline">Contact Us</MagneticButton>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  </>
);
